/**
 * Panel de búsqueda del encabezado.
 *
 * Las sugerencias salen de /search/suggest.json, la API de Shopify. No hay
 * índice propio que mantener ni app que instalar, y por eso el panel funciona
 * igual el día que se desinstale cualquier app de búsqueda.
 *
 * Los precios de las sugerencias iniciales los formatea Liquid. Los de la
 * búsqueda en vivo no puede: /search/suggest.json devuelve el precio CRUDO en
 * unidades mayores —«189.00», sin símbolo ni moneda—. Se reusa
 * NanahCart.formatoMoneda, que ya está sembrado con el money_format real de la
 * tienda, para no escribir una segunda implementación de moneda.
 *
 * Sin JS el <form> sigue apuntando a /search: la búsqueda nunca depende de
 * este archivo.
 */
class SearchPanel extends HTMLElement {
  connectedCallback() {
    this.dialogo = this.querySelector('[data-buscar-dialogo]');
    this.campo = this.querySelector('[data-buscar-campo]');
    this.vacio = this.querySelector('[data-buscar-vacio]');
    this.res = this.querySelector('[data-buscar-resultados]');
    this.lista = this.querySelector('[data-buscar-lista]');
    this.titulo = this.querySelector('[data-buscar-titulo]');
    this.estado = this.querySelector('[data-buscar-estado]');
    if (!this.dialogo) return;

    this.querySelector('[data-buscar-abrir]').addEventListener('click', () => this.abrir());
    this.querySelector('[data-buscar-cerrar]').addEventListener('click', () => this.dialogo.close());

    // Clic en el fondo del diálogo, fuera de la caja: cierra.
    this.dialogo.addEventListener('click', (e) => {
      if (e.target === this.dialogo) this.dialogo.close();
    });

    this.campo.addEventListener('input', () => this.programar());
    this.temporizador = null;
    this.peticion = null;
  }

  abrir() {
    this.dialogo.showModal();
    // El foco va al campo, no al primer botón: quien abre el panel viene a
    // escribir. En móvil esto además levanta el teclado de una vez.
    this.campo.focus();
  }

  /**
   * Espera 220 ms desde la última tecla. Sin esto se dispara una petición por
   * carácter y llegan desordenadas.
   */
  programar() {
    clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => this.buscar(), 220);
  }

  async buscar() {
    const q = this.campo.value.trim();

    if (q.length < 2) {
      this.vacio.hidden = false;
      this.res.hidden = true;
      return;
    }

    // Cancela la petición anterior: sin esto, una respuesta lenta de hace tres
    // teclas puede pisar a la más reciente.
    if (this.peticion) this.peticion.abort();
    this.peticion = new AbortController();

    const url =
      `${window.Shopify.routes.root}search/suggest.json` +
      `?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;

    try {
      const r = await fetch(url, { signal: this.peticion.signal });
      if (!r.ok) throw new Error(r.status);
      const datos = await r.json();
      this.pintar(datos.resources.results.products || [], q);
    } catch (e) {
      if (e.name === 'AbortError') return;
      // Si la API falla, el panel no se queda mudo: el formulario sigue vivo y
      // enviar lleva a la página de resultados.
      console.error('[buscador]', e);
      this.vacio.hidden = false;
      this.res.hidden = true;
    }
  }

  pintar(productos, q) {
    this.lista.innerHTML = productos
      .map((p) => {
        const foto = p.featured_image?.url
          ? `<img src="${p.featured_image.url}&width=120" alt="" loading="lazy" width="44" height="44">`
          : '';
        // La API da unidades mayores; formatoMoneda espera centavos.
        const centavos = Math.round(parseFloat(p.price) * 100);
        const precio =
          window.NanahCart && window.NanahCart.formatoMoneda
            ? window.NanahCart.formatoMoneda(centavos)
            : p.price;
        return (
          `<li><a href="${p.url}">${foto}` +
          `<span class="bpanel__n">${p.title}</span>` +
          `<span class="bpanel__p">${precio}</span></a></li>`
        );
      })
      .join('');

    this.titulo.textContent = this.dataset.tituloResultados || '';
    this.vacio.hidden = true;
    this.res.hidden = false;

    // Se limpia antes de escribir para que el lector de pantalla anuncie
    // incluso cuando el número de resultados no cambia entre búsquedas.
    this.estado.textContent = '';
    requestAnimationFrame(() => {
      this.estado.textContent = (this.dataset.plantillaEstado || '{n} · {q}')
        .replace('{n}', productos.length)
        .replace('{q}', q);
    });
  }
}

if (!customElements.get('search-panel')) {
  customElements.define('search-panel', SearchPanel);
}
